import ApisServiceApp from './ApisServiceApp'
import { apiUrl } from '@/config'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse } from '@/utils/apiResponseUtils'
import { maGocTuHangHoa } from '@/utils/maHangHienThi'
import { fetchDatHangNccDetail } from './datHangNccApi'
import type {
  IPhieuNhanHang,
  IPhieuNhanHangLine,
  IPhieuNhanHangLinePayload,
  IPhieuNhanHangPayload,
  PhieuNhanHangId,
  PhieuNhanTrangThai,
} from '@/interfaces/phieuNhanHangInterfaces'
import queryString from 'query-string'

const PREFIX = '/phieu-nhan-hang'

const blobResponseConfig = { responseType: 'blob' as const }

const parseBlobErrorIfJson = async (blob: Blob): Promise<never> => {
  const text = await blob.text()
  try {
    const j = JSON.parse(text) as { message?: string }
    throw new Error(j?.message || 'Yêu cầu thất bại')
  } catch (e) {
    if (e instanceof SyntaxError) throw new Error(text.trim().slice(0, 240) || 'Yêu cầu thất bại')
    throw e
  }
}

const ensureExcelBlob = async (blob: Blob): Promise<Blob> => {
  if (blob.type && blob.type.includes('application/json')) await parseBlobErrorIfJson(blob)
  return blob
}

function parseNum(raw: unknown): number | null {
  if (raw === '' || raw == null) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function parseStr(raw: unknown): string | null {
  return raw != null && String(raw).trim() ? String(raw) : null
}

function parseDateStr(raw: unknown): string | null {
  const s = parseStr(raw)
  return s ? s.slice(0, 10) : null
}

function normalizeTrangThai(raw: unknown): {
  trangThai: PhieuNhanTrangThai
  trangThaiRaw: string | null
} {
  const s0 = raw != null ? String(raw) : ''
  const s = s0.trim()
  if (!s) return { trangThai: 'choNhan', trangThaiRaw: null }
  const u = s.toUpperCase()
  if (u === 'CHONHAN' || u === 'CHO_NHAN') return { trangThai: 'choNhan', trangThaiRaw: s0 }
  if (u === 'DANHANXONG' || u === 'DA_NHAN_XONG') return { trangThai: 'daNhanXong', trangThaiRaw: s0 }
  if (u === 'DAHUY' || u === 'DA_HUY') return { trangThai: 'daHuy', trangThaiRaw: s0 }
  if (u.includes('HUY') || u.includes('CANCEL')) return { trangThai: 'daHuy', trangThaiRaw: s0 }
  if (u.includes('NHAN') && u.includes('XONG')) return { trangThai: 'daNhanXong', trangThaiRaw: s0 }
  if (u.includes('CHO') && u.includes('NHAN')) return { trangThai: 'choNhan', trangThaiRaw: s0 }
  return { trangThai: 'khac', trangThaiRaw: s0 }
}

function mapLine(raw: Record<string, unknown>): IPhieuNhanHangLine {
  const hangHoa =
    raw.hangHoa && typeof raw.hangHoa === 'object'
      ? (raw.hangHoa as Record<string, unknown>)
      : {}
  return {
    id: raw.id != null ? String(raw.id) : undefined,
    hangHoaId: String(raw.hangHoaId ?? ''),
    chiTietDatHangId:
      raw.chiTietDatHangId != null ? String(raw.chiTietDatHangId) : null,
    maHang: String(raw.maHang ?? ''),
    hangHoaGocMa: maGocTuHangHoa(hangHoa),
    tenHang: String(raw.tenHang ?? ''),
    donViTinh: raw.donViTinh != null ? String(raw.donViTinh) : null,
    soLuongDat: parseNum(raw.soLuongDat) ?? 0,
    soLuongNhan: parseNum(raw.soLuongNhan) ?? 0,
    donGia: parseNum(raw.donGia) ?? 0,
    maLo: raw.maLo != null && String(raw.maLo).trim() ? String(raw.maLo) : null,
    hanSuDung:
      raw.hanSuDung != null && String(raw.hanSuDung).trim()
        ? String(raw.hanSuDung).slice(0, 10)
        : null,
    quanLyTheoHanSuDung: Boolean(
      hangHoa.quanLyTheoHanSuDung ?? raw.quanLyTheoHanSuDung ?? false,
    ),
    // Field tích hợp service mua hàng (mirror PKCT + packing)
    hinhThucVc: parseStr(raw.hinhThucVc),
    cont: parseStr(raw.cont),
    maVanChuyenNcc: parseStr(raw.maVanChuyenNcc),
    maVanChuyenAzl: parseStr(raw.maVanChuyenAzl),
    maYcmh: parseStr(raw.maYcmh),
    maBaoGia: parseStr(raw.maBaoGia),
    ngayBaoGia: parseDateStr(raw.ngayBaoGia),
    donViTinhMua: parseStr(raw.donViTinhMua),
    soLuongKhuyenMai: parseNum(raw.soLuongKhuyenMai) ?? 0,
    tongSoLuongSanPham: parseNum(raw.tongSoLuongSanPham) ?? 0,
    soKien: parseNum(raw.soKien),
    daiThung: parseNum(raw.daiThung),
    rongThung: parseNum(raw.rongThung),
    caoThung: parseNum(raw.caoThung),
    theTichThung: parseNum(raw.theTichThung),
    canNangThung: parseNum(raw.canNangThung),
    quyCachThung: parseStr(raw.quyCachThung),
    canNangSanPham: parseNum(raw.canNangSanPham),
    theTichSanPham: parseNum(raw.theTichSanPham),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    stt: parseNum(raw.stt) ?? undefined,
  }
}

function mapLines(raw: unknown): IPhieuNhanHangLine[] {
  if (!Array.isArray(raw)) return []
  return raw.map((r) => mapLine((r && typeof r === 'object' ? r : {}) as Record<string, unknown>))
}

function nccTenFromRaw(raw: Record<string, unknown>): string | null {
  const n = raw.nhaCungCap
  if (n && typeof n === 'object') {
    const o = n as Record<string, unknown>
    const t = o.tenNhaCungCap ?? o.ten
    if (t != null && String(t).trim()) return String(t)
  }
  return raw.tenNhaCungCap != null ? String(raw.tenNhaCungCap) : null
}

function chiNhanhTenFromRaw(raw: Record<string, unknown>): string | null {
  const n = raw.chiNhanh
  if (n && typeof n === 'object') {
    const t = (n as Record<string, unknown>).ten
    if (t != null && String(t).trim()) return String(t)
  }
  return raw.tenChiNhanh != null && String(raw.tenChiNhanh).trim()
    ? String(raw.tenChiNhanh)
    : null
}

function maDatHangFromRaw(raw: Record<string, unknown>): string | null {
  const d = raw.datHangNCC
  if (d && typeof d === 'object') {
    const o = d as Record<string, unknown>
    if (o.maDatHang != null && String(o.maDatHang).trim()) return String(o.maDatHang)
  }
  return raw.maDatHang != null ? String(raw.maDatHang) : null
}

export function mapPhieuNhanHangFromApi(raw: Record<string, unknown>): IPhieuNhanHang {
  const { trangThai, trangThaiRaw } = normalizeTrangThai(raw.trangThai)
  return {
    id: String(raw.id ?? ''),
    maPhieuNhan: String(raw.maPhieuNhan ?? ''),
    datHangNCCId:
      raw.datHangNCCId != null && String(raw.datHangNCCId) !== ''
        ? String(raw.datHangNCCId)
        : null,
    maDatHang: maDatHangFromRaw(raw),
    nhaCungCapId:
      raw.nhaCungCapId != null && String(raw.nhaCungCapId) !== ''
        ? String(raw.nhaCungCapId)
        : '',
    tenNhaCungCap: nccTenFromRaw(raw),
    chiNhanhId: parseNum(raw.chiNhanhId) ?? 0,
    tenChiNhanh: chiNhanhTenFromRaw(raw),
    duAnId: parseNum(raw.duAnId) ?? 0,
    ngayNhan: parseStr(raw.ngayNhan),
    nguoiNhanId: parseNum(raw.nguoiNhanId),
    // Field tích hợp service mua hàng (snapshot từ muahang)
    soRef: parseStr(raw.soRef),
    ngayDatHangThucTe: parseDateStr(raw.ngayDatHangThucTe),
    ngayPhatHangThucTe: parseDateStr(raw.ngayPhatHangThucTe),
    ngayVeKhoThucTe: parseDateStr(raw.ngayVeKhoThucTe),
    tenNhanVienMuaHang: parseStr(raw.tenNhanVienMuaHang),
    tenNhanVienYeuCau: parseStr(raw.tenNhanVienYeuCau),
    tongTheTich: parseNum(raw.tongTheTich),
    tongCanNang: parseNum(raw.tongCanNang),
    tongSoLuong: parseNum(raw.tongSoLuong),
    tongTienHang: parseNum(raw.tongTienHang),
    canTraNCC: parseNum(raw.canTraNCC),
    nguonHeThong: parseStr(raw.nguonHeThong),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai,
    trangThaiRaw,
    danhSachNhan: mapLines(raw.chiTiet ?? raw.danhSachNhan),
    phieuKhos: Array.isArray(raw.phieuKhos)
      ? (raw.phieuKhos as IPhieuNhanHang['phieuKhos'])
      : [],
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
  }
}

function toLinePayload(x: IPhieuNhanHangLinePayload): Record<string, unknown> {
  const strIf = (v: string | null | undefined) =>
    v != null && String(v).trim() ? String(v).trim() : undefined
  const numIf = (v: number | null | undefined) =>
    v != null && Number.isFinite(Number(v)) ? Number(v) : undefined
  return {
    hangHoaId: String(x.hangHoaId),
    maHang: String(x.maHang),
    tenHang: String(x.tenHang),
    soLuongDat: Number(x.soLuongDat) || 0,
    soLuongNhan: Number(x.soLuongNhan) || 0,
    donGia: Number(x.donGia) || 0,
    ...(x.donViTinh != null && String(x.donViTinh).trim() ? { donViTinh: String(x.donViTinh).trim() } : {}),
    ...(x.chiTietDatHangId != null && String(x.chiTietDatHangId).trim() ? { chiTietDatHangId: String(x.chiTietDatHangId).trim() } : {}),
    ...(x.maLo != null && String(x.maLo).trim() ? { maLo: String(x.maLo).trim() } : {}),
    ...(x.hanSuDung != null && String(x.hanSuDung).trim() ? { hanSuDung: String(x.hanSuDung).slice(0, 10) } : {}),
    ...(x.ghiChu != null && String(x.ghiChu).trim() ? { ghiChu: String(x.ghiChu).trim() } : {}),
    // Field tích hợp service mua hàng (mirror PKCT + packing)
    ...(strIf(x.hinhThucVc) ? { hinhThucVc: strIf(x.hinhThucVc) } : {}),
    ...(strIf(x.cont) ? { cont: strIf(x.cont) } : {}),
    ...(strIf(x.maVanChuyenNcc) ? { maVanChuyenNcc: strIf(x.maVanChuyenNcc) } : {}),
    ...(strIf(x.maVanChuyenAzl) ? { maVanChuyenAzl: strIf(x.maVanChuyenAzl) } : {}),
    ...(strIf(x.maYcmh) ? { maYcmh: strIf(x.maYcmh) } : {}),
    ...(strIf(x.maBaoGia) ? { maBaoGia: strIf(x.maBaoGia) } : {}),
    ...(strIf(x.ngayBaoGia) ? { ngayBaoGia: strIf(x.ngayBaoGia)!.slice(0, 10) } : {}),
    ...(strIf(x.donViTinhMua) ? { donViTinhMua: strIf(x.donViTinhMua) } : {}),
    ...(numIf(x.soLuongKhuyenMai) != null ? { soLuongKhuyenMai: numIf(x.soLuongKhuyenMai) } : {}),
    ...(numIf(x.tongSoLuongSanPham) != null ? { tongSoLuongSanPham: numIf(x.tongSoLuongSanPham) } : {}),
    ...(numIf(x.soKien) != null ? { soKien: numIf(x.soKien) } : {}),
    ...(numIf(x.daiThung) != null ? { daiThung: numIf(x.daiThung) } : {}),
    ...(numIf(x.rongThung) != null ? { rongThung: numIf(x.rongThung) } : {}),
    ...(numIf(x.caoThung) != null ? { caoThung: numIf(x.caoThung) } : {}),
    ...(numIf(x.theTichThung) != null ? { theTichThung: numIf(x.theTichThung) } : {}),
    ...(numIf(x.canNangThung) != null ? { canNangThung: numIf(x.canNangThung) } : {}),
    ...(strIf(x.quyCachThung) ? { quyCachThung: strIf(x.quyCachThung) } : {}),
    ...(numIf(x.canNangSanPham) != null ? { canNangSanPham: numIf(x.canNangSanPham) } : {}),
    ...(numIf(x.theTichSanPham) != null ? { theTichSanPham: numIf(x.theTichSanPham) } : {}),
  }
}

function toPhieuNhanBody(body: IPhieuNhanHangPayload): Record<string, unknown> {
  const strIf = (v: string | null | undefined) =>
    v != null && String(v).trim() ? String(v).trim() : undefined
  const numIf = (v: number | null | undefined) =>
    v != null && Number.isFinite(Number(v)) ? Number(v) : undefined
  const out: Record<string, unknown> = {
    ...(body.datHangNCCId?.trim()
      ? { datHangNCCId: body.datHangNCCId.trim() }
      : body.nhaCungCapId?.trim()
        ? { nhaCungCapId: body.nhaCungCapId.trim() }
        : {}),
    ...(body.maPhieuNhan?.trim() ? { maPhieuNhan: body.maPhieuNhan.trim() } : {}),
    ...(body.ngayNhan?.trim() ? { ngayNhan: body.ngayNhan.trim() } : {}),
    ...(body.nguoiNhanId?.trim() ? { nguoiNhanId: body.nguoiNhanId.trim() } : {}),
    ...(body.ghiChu?.trim() ? { ghiChu: body.ghiChu.trim() } : {}),
    // Field tích hợp service mua hàng (snapshot từ muahang)
    ...(strIf(body.soRef) ? { soRef: strIf(body.soRef) } : {}),
    ...(strIf(body.ngayDatHangThucTe) ? { ngayDatHangThucTe: strIf(body.ngayDatHangThucTe)!.slice(0, 10) } : {}),
    ...(strIf(body.ngayPhatHangThucTe) ? { ngayPhatHangThucTe: strIf(body.ngayPhatHangThucTe)!.slice(0, 10) } : {}),
    ...(strIf(body.ngayVeKhoThucTe) ? { ngayVeKhoThucTe: strIf(body.ngayVeKhoThucTe)!.slice(0, 10) } : {}),
    ...(strIf(body.tenNhanVienMuaHang) ? { tenNhanVienMuaHang: strIf(body.tenNhanVienMuaHang) } : {}),
    ...(strIf(body.tenNhanVienYeuCau) ? { tenNhanVienYeuCau: strIf(body.tenNhanVienYeuCau) } : {}),
    ...(numIf(body.tongTheTich) != null ? { tongTheTich: numIf(body.tongTheTich) } : {}),
    ...(numIf(body.tongCanNang) != null ? { tongCanNang: numIf(body.tongCanNang) } : {}),
    ...(body.danhSachNhan ? { danhSachNhan: body.danhSachNhan.map(toLinePayload) } : {}),
  }
  return out
}

function resolveApiBase(): string {
  const fromEnv = (apiUrl || '').replace(/\/$/, '')
  const fromAxios = String(ApisServiceApp.defaults.baseURL || '').replace(/\/$/, '')
  return fromEnv || fromAxios
}

export function phieuNhanHangListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const datHangNCCId = q.datHangNCCId
  if (typeof datHangNCCId === 'string' && datHangNCCId.trim()) out.datHangNCCId = datHangNCCId.trim()
  const nhaCungCapId = q.nhaCungCapId
  if (typeof nhaCungCapId === 'string' && nhaCungCapId.trim()) out.nhaCungCapId = nhaCungCapId.trim()
  if (typeof q.chiNhanhId === 'string' && q.chiNhanhId.trim()) {
    out.chiNhanhId = q.chiNhanhId.trim()
  } else if (Array.isArray(q.chiNhanhId)) {
    const ids = q.chiNhanhId.map(String).filter(Boolean).join(',')
    if (ids) out.chiNhanhId = ids
  }
  const trangThai = q.trangThai
  if (typeof trangThai === 'string' && trangThai.trim()) out.trangThai = trangThai.trim()
  const maVanChuyenAzl = q.maVanChuyenAzl
  if (typeof maVanChuyenAzl === 'string' && maVanChuyenAzl.trim()) out.maVanChuyenAzl = maVanChuyenAzl.trim()
  const maYcmh = q.maYcmh
  if (typeof maYcmh === 'string' && maYcmh.trim()) out.maYcmh = maYcmh.trim()
  const tuNgay = q.tuNgay
  if (typeof tuNgay === 'string' && tuNgay.trim()) out.tuNgay = tuNgay.trim()
  const denNgay = q.denNgay
  if (typeof denNgay === 'string' && denNgay.trim()) out.denNgay = denNgay.trim()
  const sortField = q.sortField
  if (typeof sortField === 'string' && sortField.trim()) out.sortField = sortField.trim()
  const sortOrder = q.sortOrder
  if (sortOrder === 'asc' || sortOrder === 'desc') out.sortOrder = sortOrder
  return out
}

export function fetchPhieuNhanHangList(search: string) {
  const params = phieuNhanHangListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-phieu-nhan-hang`, { params }).then((res: unknown) => {
      const { data, paging, summary } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: data.map((r) => mapPhieuNhanHangFromApi(r as Record<string, unknown>)),
        paging,
        summary,
      }
    }),
  )
}

export function fetchPhieuNhanHangDetail(id: PhieuNhanHangId) {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-phieu-nhan-hang`).then((res: unknown) => {
      const raw = normalizeDetailResponse<Record<string, unknown>>(res as { data?: Record<string, unknown> })
      return { data: mapPhieuNhanHangFromApi(raw) }
    }),
  )
}

export function taoPhieuNhanHang(body: IPhieuNhanHangPayload) {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-phieu-nhan-hang`, toPhieuNhanBody(body)).then(
      (res: unknown) => normalizeDetailResponse<IPhieuNhanHang>(res as { data?: IPhieuNhanHang }),
    ),
  )
}

export function capNhatPhieuNhanHang(id: PhieuNhanHangId, body: Partial<IPhieuNhanHangPayload>) {
  const rest = toPhieuNhanBody({ danhSachNhan: [], ...body })
  // datHangNCCId immutable — strip khỏi update payload
  delete rest.datHangNCCId
  return apiRequest(
    ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-phieu-nhan-hang`, rest).then((res: unknown) =>
      normalizeDetailResponse<IPhieuNhanHang>(res as { data?: IPhieuNhanHang }),
    ),
  )
}

/** Sửa tại chỗ thông tin phiếu (không đụng dòng hàng) — mọi trạng thái trừ đã hủy; đổi NCC chỉ khi chờ nhận. */
export function capNhatThongTinPhieuNhanHang(
  id: PhieuNhanHangId,
  body: {
    ghiChu?: string | null
    ngayNhan?: string | null
    soRef?: string | null
    tenNhanVienMuaHang?: string | null
    ngayVeKhoThucTe?: string | null
    nhaCungCapId?: string
  },
) {
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/cap-nhat-thong-tin-phieu-nhan-hang`, body).then(
      (res: unknown) => normalizeDetailResponse<IPhieuNhanHang>(res as { data?: IPhieuNhanHang }),
    ),
  )
}

export function xoaPhieuNhanHang(id: PhieuNhanHangId) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-phieu-nhan-hang`))
}

export function chuyenTrangThaiPhieuNhanHang(
  id: PhieuNhanHangId,
  trangThai: 'daNhanXong' | 'daHuy',
  opts?: { ngayNhan?: string | null },
) {
  const body: { trangThai: string; ngayNhan?: string } = { trangThai }
  const ngay = opts?.ngayNhan != null ? String(opts.ngayNhan).trim() : ''
  if (ngay) body.ngayNhan = ngay
  return apiRequest(
    ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-phieu-nhan-hang`, body).then(
      (res: unknown) => normalizeDetailResponse<IPhieuNhanHang>(res as { data?: IPhieuNhanHang }),
    ),
  )
}

export function hoanThanhPhieuNhanHang(id: PhieuNhanHangId, opts?: { ngayNhan?: string | null }) {
  return chuyenTrangThaiPhieuNhanHang(id, 'daNhanXong', opts)
}

export function huyPhieuNhanHang(id: PhieuNhanHangId) {
  return chuyenTrangThaiPhieuNhanHang(id, 'daHuy')
}

export async function xuatPhieuNhanTongQuanBlob(search?: string): Promise<Blob> {
  const params = search ? phieuNhanHangListQueryFromSearch(search) : {}
  const base = resolveApiBase()
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${base}${PREFIX}/xuat-excel-tong-quan`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function xuatPhieuNhanChiTietBlob(search?: string): Promise<Blob> {
  const params = search ? phieuNhanHangListQueryFromSearch(search) : {}
  const base = resolveApiBase()
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${base}${PREFIX}/xuat-excel-chi-tiet`, { ...blobResponseConfig, params }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export async function taiTemplatePhieuNhanBlob(): Promise<Blob> {
  const base = resolveApiBase()
  const blob = (await apiRequest(
    ApisServiceApp.get<Blob>(`${base}${PREFIX}/tai-template-nhan-hang`, { ...blobResponseConfig }),
  )) as unknown as Blob
  return ensureExcelBlob(blob)
}

export {
  fetchDatHangNccDetail,
}
